import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  ArrowBack,
  CheckCircle,
  TrendingUp,
  Speed,
  Security
} from '@mui/icons-material';
import AboutUs from './AboutUs';
import ContactUs from './ContactUs';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    login: '', // Can be email or username
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPublicPage, setShowPublicPage] = useState(null);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);

  useEffect(() => {
    // Check if user has seen the modal before
    const hasSeenModal = localStorage.getItem('hasSeenWhatsNew');
    if (!hasSeenModal) {
      setShowWhatsNewModal(true);
    }
  }, []);

  const handleCloseWhatsNew = () => {
    localStorage.setItem('hasSeenWhatsNew', 'true');
    setShowWhatsNewModal(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Customize error messages based on the error type
        if (response.status === 404) {
          throw new Error('User does not exist. Please contact the admin.');
        }
        if (response.status === 401) {
          // Check the actual error message from backend
          if (data.error) {
            // If error contains "Invalid email, username, or password" - user not found
            if (data.error.includes('Invalid email, username, or password')) {
              throw new Error('User does not exist. Please contact the admin.');
            }
            // If error contains "Invalid email or password" - wrong password
            if (data.error.includes('Invalid email or password')) {
              throw new Error('Incorrect password. Please try again.');
            }
          }
          // Default message for 401
          throw new Error('Incorrect password. Please try again.');
        }
        throw new Error(data.error || 'Login failed');
      }

      // Store token in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Call the success callback
      onLoginSuccess(data.user);
    } catch (err) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  if (showPublicPage === 'aboutus') {
    return (
      <Box sx={{ minHeight: '100vh', background: '#f4f6f8' }}>
        <AppBar position="static" sx={{ bgcolor: '#fff', boxShadow: '0 2px 8px #00000015' }}>
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img src={process.env.PUBLIC_URL + '/marmon-logo.png'} alt="Marmon Logo" style={{ height: 48 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0050b3', letterSpacing: 1 }}>MARMONIM</Typography>
            </Box>
            <Button 
              startIcon={<ArrowBack />}
              variant="contained" 
              onClick={() => setShowPublicPage(null)}
              sx={{ bgcolor: '#0050b3', '&:hover': { bgcolor: '#003d82' } }}
            >
              Back to Login
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <AboutUs />
        </Container>
      </Box>
    );
  }

  if (showPublicPage === 'contactus') {
    return (
      <Box sx={{ minHeight: '100vh', background: '#f4f6f8' }}>
        <AppBar position="static" sx={{ bgcolor: '#fff', boxShadow: '0 2px 8px #00000015' }}>
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img src={process.env.PUBLIC_URL + '/marmon-logo.png'} alt="Marmon Logo" style={{ height: 48 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0050b3', letterSpacing: 1 }}>MARMONIM</Typography>
            </Box>
            <Button 
              startIcon={<ArrowBack />}
              variant="contained" 
              onClick={() => setShowPublicPage(null)}
              sx={{ bgcolor: '#0050b3', '&:hover': { bgcolor: '#003d82' } }}
            >
              Back to Login
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <ContactUs />
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Logo/Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img 
              src={process.env.PUBLIC_URL + '/marmon-logo.png'} 
              alt="Marmon Logo" 
              style={{ height: 80, marginBottom: 16 }} 
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access your account
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email or Username"
              name="login"
              type="text"
              value={formData.login}
              onChange={handleChange}
              required
              disabled={loading}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Forgot Password Link */}
            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowForgotPassword(true)}
                sx={{
                  color: '#667eea',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                disabled={loading}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>
        </Paper>

        {/* Footer with Links */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowPublicPage('aboutus')}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#fff'
                }
              }}
            >
              About Us
            </Link>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowPublicPage('contactus')}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#fff'
                }
              }}
            >
              Contact Us
            </Link>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            © 2026 Marmon IM Platform
          </Typography>
        </Box>
      </Container>

      {/* What's New Modal */}
      <Dialog 
        open={showWhatsNewModal} 
        onClose={handleCloseWhatsNew}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pb: 3,
          pt: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ fontSize: '32px' }}>🎉</Box>
            <Box>
              <Typography variant="h5" fontWeight="700">
                Welcome to the New Platform!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                The old marmonim.com has been completely upgraded
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We're excited to introduce our brand new interface with significant improvements:
          </Typography>
          
          <List sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Speed sx={{ color: '#1976d2', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography variant="subtitle2" fontWeight="600">Enhanced Performance</Typography>}
                secondary="Faster loading times and smoother navigation throughout the platform"
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TrendingUp sx={{ color: '#2e7d32', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography variant="subtitle2" fontWeight="600">Modern Interface</Typography>}
                secondary="Clean, intuitive design with improved user experience and accessibility"
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CheckCircle sx={{ color: '#9c27b0', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography variant="subtitle2" fontWeight="600">New Features</Typography>}
                secondary="Advanced documentation management and enhanced API testing tools"
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Security sx={{ color: '#ed6c02', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography variant="subtitle2" fontWeight="600">Improved Security</Typography>}
                secondary="Enhanced authentication and data protection measures"
              />
            </ListItem>
          </List>
          
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: '#f5f5f5', 
            borderRadius: 1,
            borderLeft: '4px solid #667eea'
          }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Please update your bookmarks to this new URL. All your data and settings have been migrated seamlessly.
            </Typography>
          </Box>
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseWhatsNew}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Got it, Let's Explore!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Forgot Password Component
const ForgotPassword = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process request');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img 
              src={process.env.PUBLIC_URL + '/marmon-logo.png'} 
              alt="Marmon Logo" 
              style={{ height: 80, marginBottom: 16 }} 
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your email to receive a reset link
            </Typography>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Password reset link has been sent to your email. Please check your inbox.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!success ? (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                required
                disabled={loading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
              </Button>
            </form>
          ) : null}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              type="button"
              onClick={onBackToLogin}
              sx={{
                color: '#667eea',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to Login
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// Reset Password Component (for link from email)
const ResetPassword = () => {
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  // Get token from URL on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('token');
    
    if (resetToken) {
      setToken(resetToken);
      verifyToken(resetToken);
    } else {
      setError('No reset token provided');
    }
  }, []);

  const verifyToken = async (resetToken) => {
    try {
      const response = await fetch(`${API_BASE}/auth/verify-reset-token/${resetToken}`);
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Invalid or expired token');
        setTokenValid(false);
      } else {
        setTokenValid(true);
      }
    } catch (err) {
      setError('Failed to verify token');
      setTokenValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          newPassword: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(true);
      setFormData({ password: '', confirmPassword: '' });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tokenValid === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 2
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={10} sx={{ padding: 4, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" color="error" gutterBottom>
              Invalid or Expired Token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.href = '/'}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              Back to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img 
              src={process.env.PUBLIC_URL + '/marmon-logo.png'} 
              alt="Marmon Logo" 
              style={{ height: 80, marginBottom: 16 }} 
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your new password below
            </Typography>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Password reset successfully! Redirecting to login...
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!success ? (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={loading}
                sx={{ mb: 2 }}
                helperText="Minimum 6 characters"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                disabled={loading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
            </form>
          ) : null}
        </Paper>
      </Container>
    </Box>
  );
};

// Export ResetPassword as a standalone component for routing
export { ResetPassword };

export default Login;